import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import {
  Network, Database, FolderTree, Plus, Check, X, Loader2, RefreshCw, Trash2, Edit2,
  CheckCircle2, AlertTriangle, ChevronRight, Search,
} from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

// Source metadata for display
const SOURCE_META = {
  tasks:      { tint: 'emerald', emoji: 'TSK', label: 'Tasks' },
  memory:     { tint: 'violet',  emoji: 'MEM', label: 'Memory' },
  eod:        { tint: 'amber',   emoji: 'EOD', label: 'EOD' },
  calendar:   { tint: 'sky',     emoji: 'CAL', label: 'Calendar' },
  jira:       { tint: 'blue',    emoji: 'JRA', label: 'Jira' },
  confluence: { tint: 'indigo',  emoji: 'CFL', label: 'Confluence' },
  gdrive:     { tint: 'yellow',  emoji: 'DRV', label: 'Drive' },
  gdocs:      { tint: 'blue',    emoji: 'DOC', label: 'Docs' },
  gsheets:    { tint: 'green',   emoji: 'SHT', label: 'Sheets' },
  gslides:    { tint: 'orange',  emoji: 'SLD', label: 'Slides' },
  youtube:    { tint: 'red',     emoji: 'YT',  label: 'YouTube' },
};

const NODE_TYPE_LABEL = {
  work_area: 'Work Area',
  kind: 'Kind',
  date_range: 'Date Range',
  calendar: 'Calendar',
  project: 'Project',
  epic: 'Epic',
  space: 'Space',
  page: 'Page',
  drive: 'Drive',
  folder: 'Folder',
  file: 'File',
  section: 'Section',
  subscription: 'Channel',
  playlist: 'Playlist',
  video: 'Video',
};

function SourcePill({ source, connected, active, count, onClick }) {
  const meta = SOURCE_META[source] || { tint: 'slate', label: source };
  return (
    <button
      onClick={onClick}
      data-testid={`knowledge-source-pill-${source}`}
      className={[
        'inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium orka-transition-colors',
        active
          ? `border-${meta.tint}-500 bg-${meta.tint}-50 text-${meta.tint}-900 dark:bg-${meta.tint}-950/40 dark:text-${meta.tint}-100`
          : 'border orka-border-subtle hover:border-slate-400 hover:bg-slate-50 dark:hover:bg-slate-900',
        !connected && 'opacity-50'
      ].filter(Boolean).join(' ')}
    >
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${connected ? 'bg-emerald-500' : 'bg-slate-400'}`} />
      <span>{meta.label}</span>
      {typeof count === 'number' && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-800">{count}</span>
      )}
    </button>
  );
}


function SelectionChip({ sel, onRemove }) {
  const meta = SOURCE_META[sel.source] || { tint: 'slate', label: sel.source };
  return (
    <div className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs bg-${meta.tint}-50 dark:bg-${meta.tint}-950/30 border-${meta.tint}-300 dark:border-${meta.tint}-800`}>
      <span className="font-mono text-[10px] opacity-60">{meta.label}</span>
      <span className="font-medium">{sel.title || sel.node_id}</span>
      <span className="text-[10px] opacity-60">{NODE_TYPE_LABEL[sel.node_type] || sel.node_type}</span>
      {onRemove && (
        <button onClick={onRemove} className="hover:text-red-600" data-testid={`selection-remove-${sel.source}-${sel.node_id}`}>
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}


export function KnowledgeSourcesPanel() {
  const authFetch = async (url, opts = {}) => fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
    ...opts,
  });
  const [sources, setSources] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeSource, setActiveSource] = useState(null);
  const [nodes, setNodes] = useState([]);  // loaded for current tree level
  const [loadingNodes, setLoadingNodes] = useState(false);
  const [breadcrumb, setBreadcrumb] = useState([]); // [{node_id, node_type, title, meta}]
  const [selections, setSelections] = useState([]);  // [{source, node_type, node_id, title, meta}]
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null); // null for create, {group_id, ...} for edit
  const [groupName, setGroupName] = useState('');
  const [groupDesc, setGroupDesc] = useState('');

  const fetchSources = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/ai/v2/knowledge/sources`);
      const data = await r.json();
      setSources(data.sources || []);
    } catch (e) { /* no-op */ }
  }, [authFetch]);

  const fetchGroups = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/ai/v2/knowledge/groups`);
      const data = await r.json();
      setGroups(data.groups || []);
    } catch (e) { /* no-op */ }
  }, [authFetch]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchSources(), fetchGroups()]).finally(() => setLoading(false));
  }, [fetchSources, fetchGroups]);

  const loadTree = useCallback(async (sourceKey, parentCrumb) => {
    setLoadingNodes(true);
    setNodes([]);
    try {
      const params = new URLSearchParams();
      if (parentCrumb) {
        if (parentCrumb.node_id) params.set('parent_id', parentCrumb.node_id);
        if (parentCrumb.node_type) params.set('parent_type', parentCrumb.node_type);
        const driveId = parentCrumb.meta && parentCrumb.meta.drive_id;
        if (driveId) params.set('drive_id', driveId);
      }
      const qs = params.toString();
      const url = `${API}/ai/v2/knowledge/sources/${sourceKey}/tree${qs ? `?${qs}` : ''}`;
      const r = await authFetch(url);
      const data = await r.json();
      setNodes(data.nodes || []);
    } catch (e) {
      toast.error(`Could not load ${sourceKey}: ${e.message || e}`);
    } finally {
      setLoadingNodes(false);
    }
  }, [authFetch]);

  const openSource = useCallback(async (sourceKey) => {
    setActiveSource(sourceKey);
    setBreadcrumb([]);
    await loadTree(sourceKey, null);
  }, [loadTree]);

  const drillInto = useCallback(async (node) => {
    const newCrumb = [...breadcrumb, node];
    setBreadcrumb(newCrumb);
    await loadTree(activeSource, node);
  }, [activeSource, breadcrumb, loadTree]);

  const goToCrumb = useCallback(async (index) => {
    // index = -1 means root of the source; index 0..n-1 means that crumb
    const newCrumb = index < 0 ? [] : breadcrumb.slice(0, index + 1);
    setBreadcrumb(newCrumb);
    const parent = newCrumb.length ? newCrumb[newCrumb.length - 1] : null;
    await loadTree(activeSource, parent);
  }, [activeSource, breadcrumb, loadTree]);

  const toggleSelection = (source, node) => {
    const nid = node.node_id;
    const idx = selections.findIndex(s => s.source === source && s.node_id === nid);
    if (idx >= 0) {
      setSelections(selections.filter((_, i) => i !== idx));
    } else {
      setSelections([...selections, {
        source,
        node_type: node.node_type,
        node_id: nid,
        title: node.title,
        meta: node.meta || {},
      }]);
    }
  };

  const isSelected = (source, nid) =>
    selections.some(s => s.source === source && s.node_id === nid);

  const openCreateDialog = () => {
    setEditingGroup(null);
    setGroupName('');
    setGroupDesc('');
    // keep current selections as starting cart
    setDialogOpen(true);
  };

  const openEditDialog = (g) => {
    setEditingGroup(g);
    setGroupName(g.name || '');
    setGroupDesc(g.description || '');
    setSelections((g.selections || []).map(s => ({ ...s })));
    setDialogOpen(true);
  };

  const saveGroup = async () => {
    if (!groupName.trim()) {
      toast.error('Name required');
      return;
    }
    try {
      const body = {
        name: groupName.trim(),
        description: groupDesc.trim(),
        selections,
      };
      const url = editingGroup
        ? `${API}/ai/v2/knowledge/groups/${editingGroup.group_id}`
        : `${API}/ai/v2/knowledge/groups`;
      const method = editingGroup ? 'PUT' : 'POST';
      const r = await authFetch(url, { method, body: JSON.stringify(body) });
      if (!r.ok) throw new Error((await r.json())?.detail || 'save failed');
      toast.success(editingGroup ? 'Group updated' : 'Group created');
      setDialogOpen(false);
      setSelections([]);
      await fetchGroups();
    } catch (e) {
      toast.error(`Save failed: ${e.message || e}`);
    }
  };

  const deleteGroup = async (group_id) => {
    if (!confirm('Delete this knowledge group?')) return;
    try {
      await authFetch(`${API}/ai/v2/knowledge/groups/${group_id}`, { method: 'DELETE' });
      toast.success('Deleted');
      await fetchGroups();
    } catch (e) { toast.error('Delete failed'); }
  };

  const clearSelections = () => setSelections([]);

  return (
    <div className="space-y-6" data-testid="knowledge-sources-panel">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Network className="w-5 h-5 text-violet-600" />
                Knowledge Sources
              </CardTitle>
              <CardDescription className="mt-1 max-w-2xl">
                Visually bundle the places Orka AI can read from. Select items in
                one or many sources, then save the bundle as a <em>Knowledge Group</em> —
                e.g. "HR Knowledge" = Confluence HRA space + Drive HR folder + Jira HR project.
                The orchestrator can then use the group as a single named scope.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => { fetchSources(); fetchGroups(); }}
              data-testid="knowledge-refresh-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Source pills */}
          <div className="flex flex-wrap gap-2" data-testid="knowledge-source-pills">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {sources.map(s => (
              <SourcePill
                key={s.key}
                source={s.key}
                connected={s.connected !== false}
                active={activeSource === s.key}
                onClick={() => openSource(s.key)}
              />
            ))}
          </div>

          {/* Active source tree */}
          {activeSource && (
            <Card className="bg-slate-50 dark:bg-slate-900/50 border-dashed">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-sm font-medium min-w-0">
                    <FolderTree className="w-4 h-4 flex-shrink-0" />
                    <button
                      onClick={() => goToCrumb(-1)}
                      data-testid="knowledge-breadcrumb-root"
                      className="hover:underline font-semibold"
                    >
                      {SOURCE_META[activeSource]?.label || activeSource}
                    </button>
                    {breadcrumb.map((c, i) => (
                      <React.Fragment key={`${c.node_type}-${c.node_id}`}>
                        <ChevronRight className="w-3 h-3 text-slate-400 flex-shrink-0" />
                        <button
                          onClick={() => goToCrumb(i)}
                          data-testid={`knowledge-breadcrumb-${i}`}
                          className="hover:underline truncate max-w-[140px]"
                          title={c.title}
                        >
                          {c.title}
                        </button>
                      </React.Fragment>
                    ))}
                    <Badge variant="secondary" className="text-[10px] ml-1">
                      {nodes.length} items
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const s = sources.find(x => x.key === activeSource);
                      if (!s) return null;
                      if (!s.connected) return <Badge variant="outline" className="text-amber-700 border-amber-500">Not connected</Badge>;
                      return null;
                    })()}
                    <Button size="sm" variant="ghost" onClick={() => { setActiveSource(null); setBreadcrumb([]); }}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                {loadingNodes ? (
                  <div className="py-6 text-center text-sm text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                  </div>
                ) : nodes.length === 0 ? (
                  <div className="py-6 text-center text-sm text-slate-500">
                    {breadcrumb.length === 0
                      ? 'No items found. Ensure the integration is connected and has access.'
                      : 'No children here.'}
                  </div>
                ) : (
                  <ScrollArea className="max-h-[360px]">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {nodes.map(n => {
                        const sel = isSelected(activeSource, n.node_id);
                        return (
                          <div
                            key={`${activeSource}-${n.node_id}`}
                            data-testid={`knowledge-node-${activeSource}-${n.node_id}`}
                            className={[
                              'group flex items-center justify-between rounded-md border px-3 py-2 text-left text-sm orka-transition-colors',
                              sel
                                ? 'border-violet-500 bg-violet-50 dark:bg-violet-950/30 dark:border-violet-700'
                                : 'border orka-border-subtle hover:bg-slate-100 dark:hover:bg-slate-800'
                            ].join(' ')}
                          >
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <span
                                role="checkbox"
                                aria-checked={sel}
                                tabIndex={0}
                                onClick={(e) => { e.stopPropagation(); toggleSelection(activeSource, n); }}
                                onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggleSelection(activeSource, n); } }}
                                data-testid={`knowledge-node-toggle-${activeSource}-${n.node_id}`}
                                className="flex-shrink-0 cursor-pointer"
                                aria-label={sel ? 'Deselect' : 'Select'}
                              >
                                <Checkbox checked={sel} className="pointer-events-none" />
                              </span>
                              <button
                                onClick={() => (n.has_children ? drillInto(n) : toggleSelection(activeSource, n))}
                                data-testid={`knowledge-node-open-${activeSource}-${n.node_id}`}
                                className="truncate text-left flex-1 min-w-0"
                              >
                                <div className="font-medium truncate">{n.title || n.node_id}</div>
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 truncate">
                                  {NODE_TYPE_LABEL[n.node_type] || n.node_type} · {n.node_id}
                                </div>
                              </button>
                            </div>
                            {n.has_children && (
                              <button
                                onClick={() => drillInto(n)}
                                data-testid={`knowledge-node-drill-${activeSource}-${n.node_id}`}
                                className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 flex-shrink-0"
                                aria-label="Browse children"
                              >
                                <ChevronRight className="w-3.5 h-3.5 text-slate-500 group-hover:text-slate-700" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          )}

          {/* Selection cart */}
          <div className="border-t pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium">
                Current selection
                {selections.length > 0 && (
                  <Badge variant="secondary" className="ml-2 text-[10px]">{selections.length}</Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {selections.length > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearSelections}
                    data-testid="knowledge-clear-selection-btn">
                    Clear
                  </Button>
                )}
                <Button size="sm" onClick={openCreateDialog} disabled={selections.length === 0}
                  data-testid="knowledge-save-group-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Save as Group
                </Button>
              </div>
            </div>
            {selections.length === 0 ? (
              <div className="text-xs text-slate-500 py-2">
                Click a source above, then tap items to add them. When you're ready, save the whole selection as a named Knowledge Group.
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5" data-testid="knowledge-selections-list">
                {selections.map((s, i) => (
                  <SelectionChip key={`${s.source}-${s.node_id}`} sel={s}
                    onRemove={() => setSelections(selections.filter((_, idx) => idx !== i))}
                  />
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Saved Groups */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5 text-emerald-600" />
            Knowledge Groups
            {groups.length > 0 && <Badge variant="secondary">{groups.length}</Badge>}
          </CardTitle>
          <CardDescription>
            Your named bundles. The orchestrator uses these when you say things like "from HR Knowledge, find…".
          </CardDescription>
        </CardHeader>
        <CardContent>
          {groups.length === 0 ? (
            <div className="text-sm text-slate-500 py-4 text-center">
              No groups yet. Select some items above and hit "Save as Group".
            </div>
          ) : (
            <div className="space-y-3" data-testid="knowledge-groups-list">
              {groups.map(g => (
                <div key={g.group_id} data-testid={`knowledge-group-${g.group_id}`}
                  className="border rounded-lg p-3 hover:border-violet-400 dark:hover:border-violet-700 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{g.name}</span>
                        <span className="text-[10px] font-mono text-slate-500">{g.group_id}</span>
                      </div>
                      {g.description && (
                        <div className="text-xs orka-text-secondary mt-0.5">{g.description}</div>
                      )}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(g.selections || []).slice(0, 12).map((s, i) => (
                          <SelectionChip key={`${s.source}-${s.node_id}-${i}`} sel={s} />
                        ))}
                        {(g.selections || []).length > 12 && (
                          <span className="text-[10px] text-slate-500">+{g.selections.length - 12} more</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(g)}
                        data-testid={`knowledge-group-edit-${g.group_id}`}>
                        <Edit2 className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteGroup(g.group_id)}
                        data-testid={`knowledge-group-delete-${g.group_id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="knowledge-group-dialog">
          <DialogHeader>
            <DialogTitle>{editingGroup ? 'Edit Knowledge Group' : 'New Knowledge Group'}</DialogTitle>
            <DialogDescription>
              Name this bundle so the AI can use it by reference.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium orka-text-secondary">Name</label>
              <Input value={groupName} onChange={(e) => setGroupName(e.target.value)}
                placeholder="e.g. HR Knowledge" data-testid="knowledge-group-name-input" />
            </div>
            <div>
              <label className="text-xs font-medium orka-text-secondary">Description (optional)</label>
              <Textarea value={groupDesc} onChange={(e) => setGroupDesc(e.target.value)}
                placeholder="What should the AI know about this bundle?"
                data-testid="knowledge-group-desc-input" rows={2} />
            </div>
            <div>
              <label className="text-xs font-medium orka-text-secondary">
                Selections ({selections.length})
              </label>
              <div className="flex flex-wrap gap-1 mt-1">
                {selections.length === 0 && (
                  <span className="text-xs text-slate-500">No items selected. Close this dialog and click items in a source.</span>
                )}
                {selections.map((s, i) => (
                  <SelectionChip key={`dlg-${s.source}-${s.node_id}-${i}`} sel={s}
                    onRemove={() => setSelections(selections.filter((_, idx) => idx !== i))}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveGroup} disabled={!groupName.trim()}
              data-testid="knowledge-group-save-btn">
              {editingGroup ? 'Save changes' : 'Create group'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default KnowledgeSourcesPanel;
